/**
 * CSS Variable Injection System
 *
 * Injects theme colors as CSS custom properties (variables) on the document root.
 * This allows components to use var(--header-color) etc. for theme-aware styling.
 *
 * V2 Update: Added support for shadows, gradients, radii, and transitions
 */

import {
  CSS_VARIABLE_NAMES,
  COLOR_TO_CSS_VAR,
  SHADOW_TO_CSS_VAR,
  RADIUS_TO_CSS_VAR,
  TRANSITION_TO_CSS_VAR
} from './types'
import { getThemeConfigByName, isValidTheme } from './themeConfig'
// Note: Defaults are applied locally to handle the camelCase key convention
// used in theme configs. See src/constants/colorSchema.js for the canonical schema.

/**
 * Theme config color key defaults mapping (camelCase keys)
 * Maps optional color keys to their default sources
 */
const THEME_COLOR_DEFAULTS = {
  primaryActionBg: 'buttonColor',
  primaryActionBgHover: 'buttonHover',
  promoBannerBg: 'headerColor',
  promoBannerTextColor: 'lightTextColor'
}

/**
 * Apply defaults for optional color variables in theme config
 * @param {Object} colors - Theme colors object (camelCase keys)
 * @returns {Object} - Colors with defaults applied
 */
function applyColorDefaultsForTheme(colors) {
  const result = { ...colors }

  for (const [optionalKey, defaultKey] of Object.entries(THEME_COLOR_DEFAULTS)) {
    if (!result[optionalKey] && result[defaultKey]) {
      result[optionalKey] = result[defaultKey]
    }
  }

  return result
}

/**
 * Inject all CSS variables for a given theme
 *
 * @param themeName - The name of the theme to inject
 * @throws Error if theme name is invalid
 */
export function injectCSSVariables(themeName) {
  if (!isValidTheme(themeName)) {
    throw new Error(`Invalid theme name: ${themeName}`)
  }

  const themeConfig = getThemeConfigByName(themeName)
  const root = document.documentElement

  // Apply defaults for optional color variables
  const colorsWithDefaults = applyColorDefaultsForTheme(themeConfig.colors)

  // Inject each color as a CSS variable
  Object.entries(colorsWithDefaults).forEach(([colorKey, colorValue]) => {
    const cssVarName = COLOR_TO_CSS_VAR[colorKey]
    if (cssVarName) {
      root.style.setProperty(cssVarName, colorValue)
    }
  })

  // V2: Inject shadows (if present)
  if (themeConfig.shadows) {
    Object.entries(themeConfig.shadows).forEach(([shadowKey, shadowValue]) => {
      const cssVarName = SHADOW_TO_CSS_VAR[shadowKey]
      if (cssVarName) {
        root.style.setProperty(cssVarName, shadowValue)
      }
    })
  }

  // V2: Inject radii (if present)
  if (themeConfig.radii) {
    Object.entries(themeConfig.radii).forEach(([radiusKey, radiusValue]) => {
      const cssVarName = RADIUS_TO_CSS_VAR[radiusKey]
      if (cssVarName) {
        root.style.setProperty(cssVarName, radiusValue)
      }
    })
  }

  // V2: Inject transitions (if present)
  if (themeConfig.transitions) {
    Object.entries(themeConfig.transitions).forEach(([transitionKey, transitionValue]) => {
      const cssVarName = TRANSITION_TO_CSS_VAR[transitionKey]
      if (cssVarName) {
        root.style.setProperty(cssVarName, transitionValue)
      }
    })
  }

  // V2: Inject computed gradients (if gradient colors are present)
  if (themeConfig.colors.primaryGradientFrom && themeConfig.colors.primaryGradientTo) {
    root.style.setProperty(
      '--gradient-primary',
      `linear-gradient(to bottom, ${themeConfig.colors.primaryGradientFrom}, ${themeConfig.colors.primaryGradientTo})`
    )
  }

  if (themeConfig.colors.accentGradientFrom && themeConfig.colors.accentGradientTo) {
    root.style.setProperty(
      '--gradient-accent',
      `linear-gradient(to bottom, ${themeConfig.colors.accentGradientFrom}, ${themeConfig.colors.accentGradientTo})`
    )
  }

  if (themeConfig.colors.successGradientFrom && themeConfig.colors.successGradientTo) {
    root.style.setProperty(
      '--gradient-success',
      `linear-gradient(to bottom, ${themeConfig.colors.successGradientFrom}, ${themeConfig.colors.successGradientTo})`
    )
  }

  if (themeConfig.colors.surfaceGradientFrom && themeConfig.colors.surfaceGradientTo) {
    root.style.setProperty(
      '--gradient-surface',
      `linear-gradient(to bottom, ${themeConfig.colors.surfaceGradientFrom}, ${themeConfig.colors.surfaceGradientTo})`
    )
  }

  // V2: Set a flag indicating design system version
  if (themeConfig.designSystem) {
    root.setAttribute('data-design-system', themeConfig.designSystem)
  }

  // Inject raw cssVariables if present (keys are already CSS var names, e.g. '--border-radius-md')
  if (themeConfig.cssVariables) {
    Object.entries(themeConfig.cssVariables).forEach(([varName, value]) => {
      root.style.setProperty(varName, value)
    })
  }
}

/**
 * Remove all theme CSS variables from the document root
 */
export function removeCSSVariables() {
  const root = document.documentElement

  CSS_VARIABLE_NAMES.forEach(varName => {
    root.style.removeProperty(varName)
  })
}

/**
 * Get the current value of a CSS variable
 *
 * @param varName - The CSS variable name (e.g., '--header-color')
 * @returns The current value or empty string if not set
 */
export function getCSSVariableValue(varName) {
  const root = document.documentElement
  return getComputedStyle(root).getPropertyValue(varName).trim()
}

/**
 * Check if a specific CSS variable is set
 */
export function isCSSVariableSet(varName) {
  return getCSSVariableValue(varName) !== ''
}

/**
 * Get all theme CSS variables as an object
 */
export function getAllCSSVariables() {
  const result = {}

  CSS_VARIABLE_NAMES.forEach(varName => {
    result[varName] = getCSSVariableValue(varName)
  })

  return result
}
